import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { useAuth } from "../auth/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignIn() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organisation: "",
    designation: "",
    country: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/me", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="mx-auto w-full max-w-xl py-10">
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-[#361a54]">Create your account</CardTitle>
          <CardDescription>Track your learning across DPI Governance courses and case studies.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={form.name} onChange={update("name")} data-testid="register-name-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="register-email-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={update("password")} data-testid="register-password-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="organisation">Organisation</Label>
              <Input id="organisation" value={form.organisation} onChange={update("organisation")} data-testid="register-organisation-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="designation">Designation</Label>
              <Input id="designation" value={form.designation} onChange={update("designation")} data-testid="register-designation-input" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="country">Country</Label>
              <Input id="country" value={form.country} onChange={update("country")} data-testid="register-country-input" />
            </div>
            {error && <p className="md:col-span-2 text-sm text-red-700" data-testid="register-error">{error}</p>}
            <Button type="submit" className="md:col-span-2 w-full bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={loading} data-testid="register-submit-btn">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <div className="my-4 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]"
            onClick={startGoogleSignIn}
            data-testid="google-signin-btn"
          >
            Continue with Google
          </Button>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-[#1a8bb3] hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
